/**
 * Hook per controllare lo stato "prezzi disallineati" di una singola riga di
 * preventivo (estimate_stratigraphy). Granularità row.
 *
 * Confronta DUE cose con `row.prices_updated_at`:
 *  1. `materials.updated_at` dei materiali dello snapshot — cambi di prezzo;
 *  2. `stratigraphies.updated_at` della parete collegata — cambi di COMPOSIZIONE.
 *
 * Il secondo controllo mancava, ed e' il motivo per cui si poteva modificare una
 * parete (togliere un layer, cambiare il passo) senza che nessuna riga di
 * preventivo se ne accorgesse: salvare dal configuratore tocca `stratigraphies`
 * e `layers`, mai `materials`. Il salvataggio cancella e ricrea i layer nella
 * stessa operazione, quindi `stratigraphies.updated_at` e' un indicatore
 * affidabile senza dover interrogare anche i layer.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isStratigraphyPriceStale, extractMaterialIds } from '@/utils/estimates/priceStaleCheck';

export interface RowStaleStatus {
  isStale: boolean;
  /** Numero di materiali (su questa row) aggiornati dopo l'ultimo bulk update. */
  staleMaterialsCount: number;
  /** La parete collegata e' stata modificata dopo l'ultimo ricalcolo. */
  stratigraphyChanged: boolean;
}

export const useEstimateRowStaleStatus = (
  estimateStratigraphyId: string | null | undefined,
): RowStaleStatus | null => {
  const { data } = useQuery({
    queryKey: ['estimate-row-stale-status', estimateStratigraphyId],
    enabled: !!estimateStratigraphyId,
    staleTime: 60_000,
    queryFn: async (): Promise<RowStaleStatus> => {
      const vuoto: RowStaleStatus = { isStale: false, staleMaterialsCount: 0, stratigraphyChanged: false };
      if (!estimateStratigraphyId) return vuoto;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: row } = await (supabase as any)
        .from('estimate_stratigraphies')
        .select('id, prices_updated_at, stratigraphy_data, layers_data, original_stratigraphy_id')
        .eq('id', estimateStratigraphyId)
        .maybeSingle();
      if (!row) return vuoto;

      const rowPricedAt = row.prices_updated_at ? new Date(row.prices_updated_at).getTime() : 0;

      // 1) La PARETE e' cambiata dopo l'ultimo ricalcolo?
      let stratigraphyChanged = false;
      if (row.original_stratigraphy_id) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: strat } = await (supabase as any)
          .from('stratigraphies')
          .select('updated_at')
          .eq('id', row.original_stratigraphy_id)
          .maybeSingle();
        if (strat?.updated_at) {
          stratigraphyChanged = new Date(strat.updated_at).getTime() > rowPricedAt;
        }
      }

      // 2) I MATERIALI dello snapshot sono cambiati?
      const matIds = extractMaterialIds(row);
      let staleMaterialsCount = 0;
      if (matIds.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: mats } = await (supabase as any)
          .from('materials')
          .select('id, updated_at')
          .in('id', matIds);

        const matMap = new Map<string, number>();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((mats as any[] | null) ?? []).forEach(m => {
          if (m?.id && m?.updated_at) matMap.set(m.id, new Date(m.updated_at).getTime());
        });

        if (isStratigraphyPriceStale(row, matMap)) {
          for (const mid of matIds) {
            const u = matMap.get(mid);
            if (u !== undefined && u > rowPricedAt) staleMaterialsCount++;
          }
        }
      }

      return {
        isStale: stratigraphyChanged || staleMaterialsCount > 0,
        staleMaterialsCount,
        stratigraphyChanged,
      };
    },
  });

  return useMemo(() => data ?? null, [data]);
};
